import { useMemo, MouseEvent } from 'react';

import styled, { css } from 'styled-components';

import {
  EditAgentsAvailabilityProps,
  MenuOptionItemProps,
  MenuOptionsProps,
} from './EditAgentsAvailability.decl';

import { CloseCircleFilled, ClockFilled, TickCircleFilled } from '@aircall/icons';
import {
  Box,
  Button,
  Dropdown,
  DropdownButton,
  Flex,
  Menu,
  MenuItem,
  MenuItemLabel,
  MenuItemPrefix,
  MenuItemSuffix,
  Skeleton,
  Typography,
  getSpace,
} from '@aircall/tractor-v2';
import { BusinessHoursState } from '@components/BusinessHoursState/BusinessHoursState';
import { BUSINESS_HOURS_TYPE } from '@constants/generic.constants';
import { USER_DETAIL_ROUTE_TAB } from '@constants/routes.constants';
import { routeReplacer } from '@dashboard/library';
import { useAgentWorkingHours } from '@hooks/useAgentWorkingHours';
import { useAuthenticationState } from '@hooks/useAuthenticationState';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

const StyledDropdownButton = styled(DropdownButton).attrs({
  variant: 'secondary',
  mode: 'outline',
  block: true,
})<{
  buttonAnimationDisabled: boolean;
  hasPadding?: boolean;
}>`
  ${({ hasPadding }) => (!hasPadding ? 'padding-left: 0' : '')};
  justify-content: space-between;
  background-color: transparent;
  border: none;

  &:hover {
    background-color: transparent;
  }

  ${({ buttonAnimationDisabled }) =>
    buttonAnimationDisabled &&
    css`
      background-color: inherit;
      transition: none;

      &:active {
        background-color: inherit;
      }
    `}
`;

const BusinessHoursWrapper = styled(Flex)`
  height: 48px;
  align-items: center;
  padding: 0 ${getSpace('s')};
`;

function MenuOptionItem({
  to,
  name,
  description,
  onClick,
  tractorIcon,
  'data-test': dataTest,
}: MenuOptionItemProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleButtonClick = (event: MouseEvent) => {
    // prevent the parent's click event to be called
    event.stopPropagation();
    navigate(to!);
  };

  const handleMenuItemClick = (event: MouseEvent<HTMLElement>) => {
    onClick?.(event);
  };

  return (
    <MenuItem onClick={handleMenuItemClick} data-test={dataTest} itemKey={name}>
      <MenuItemPrefix>{tractorIcon}</MenuItemPrefix>
      <MenuItemLabel>
        <Flex flexDirection='column' ml='xxs'>
          <Typography variant='bodyMediumS'>{name}</Typography>
          <Typography variant='bodyRegularS' color='neutral-300'>
            {description}
          </Typography>
        </Flex>
      </MenuItemLabel>
      {to && (
        <MenuItemSuffix>
          <Button
            onClick={handleButtonClick}
            size='xSmall'
            mode='link'
            data-test={`${dataTest}-change`}
          >
            {t('app.change')}
          </Button>
        </MenuItemSuffix>
      )}
    </MenuItem>
  );
}

export function MenuOptions({ options }: MenuOptionsProps) {
  return (
    <Menu data-test='menu-options' size='large'>
      {options.map(({ name, description, tractorIcon, onClick, to, 'data-test': dataTest }) => (
        <MenuOptionItem
          key={name}
          name={name}
          description={description}
          tractorIcon={tractorIcon}
          data-test={dataTest ?? 'menu-option-item'}
          {...(onClick && { onClick })}
          {...(to && { to })}
        />
      ))}
    </Menu>
  );
}

function DropdownLoader() {
  return (
    <Flex
      justifyContent='center'
      alignItems='center'
      h={48}
      data-test='users-list-card-dropdown-loader'
    >
      <Skeleton w={100} h={18} />
    </Flex>
  );
}

export function EditAgentsAvailability({
  id,
  businessHours,
  state,
  isAvailable,
  buttonAnimationDisabled = false,
  hasPadding = false,
}: EditAgentsAvailabilityProps) {
  const {
    authState: { isCognitoMigrated },
  } = useAuthenticationState();
  const { t } = useTranslation();
  const {
    mutateBusinessHoursToOpened: handleAvailable,
    mutateBusinessHoursToClosed: handleUnavailable,
    mutateBusinessHoursToCustom: handleAutomatic,
    loading,
  } = useAgentWorkingHours({ id, businessHours });

  const options = useMemo<MenuOptionItemProps[]>(
    () => [
      {
        name: t('app.accept'),
        description: t('users.working_hours.accept.description'),
        tractorIcon: <TickCircleFilled color='primary-500' />,
        onClick: handleAvailable,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'data-test': 'menu-option-item-accept',
      },
      {
        name: t('app.auto'),
        description: t('users.working_hours.auto.description'),
        tractorIcon: <ClockFilled color='informative-500' />,
        onClick: handleAutomatic,
        to: routeReplacer(USER_DETAIL_ROUTE_TAB, { userId: id, tab: 'working-hours' }),
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'data-test': 'menu-option-item-auto',
      },
      {
        name: t('app.unavailable'),
        description: t('users.working_hours.unavailable.description'),
        tractorIcon: <CloseCircleFilled color='critical-500' />,
        onClick: handleUnavailable,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'data-test': 'menu-option-item-unavailable',
      },
    ],
    [t, handleAvailable, handleAutomatic, id, handleUnavailable]
  );

  if (loading) {
    return <DropdownLoader />;
  }

  if (!isCognitoMigrated) {
    return (
      <BusinessHoursWrapper data-test='business-hours-wrapper'>
        <BusinessHoursState
          state={state}
          tooltipTitle={t('users.card.working_hours_tooltip')}
          open={isAvailable}
          type={BUSINESS_HOURS_TYPE.AGENTS}
        />
      </BusinessHoursWrapper>
    );
  }

  return (
    <Box onClick={(e) => e.stopPropagation()}>
      <Dropdown
        data-test='users-list-card-dropdown'
        trigger={
          <StyledDropdownButton
            buttonAnimationDisabled={buttonAnimationDisabled}
            hasPadding={hasPadding}
            block
          >
            <BusinessHoursState
              state={state}
              open={isAvailable}
              type={BUSINESS_HOURS_TYPE.AGENTS}
            />
          </StyledDropdownButton>
        }
        side='bottom'
        align='start'
      >
        <MenuOptions options={options} />
      </Dropdown>
    </Box>
  );
}
